import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'mage-react-router';
import { isOverseas } from '@moka-fe/ats.utils';
import cms from '@moka-fe/cms';
import { useQuery } from '@tanstack/react-query';
import classNames from 'classnames';
import moment from 'moment';
import { ValueOf } from 'type-fest';
import {
  AutoEllipsis,
  Button,
  Dropdown,
  Empty,
  Icon,
  Loading,
  NewTable,
  Pagination,
  Spacing,
  StaticSvg,
  Tag,
} from 'sugar-design';
import * as sdf from '@SDFoundation';

import {
  ADDITIONAL_STAGES,
  DATE_FORMAT,
  DEFAULT_PAGE_LIMIT,
  HEADHUNTER_APPLICATION_STATUS,
  HEADHUNTER_CONFIRM_STATUS,
  HIRE_MODES,
  HIRE_MODE_NAMES,
} from '@/constants/common';
import useKeywordDebounce from '@/hooks/useKeywordDebounce';
import { Application, HeadhunterConfirmed } from '@/models/application';
import { Pagination as PaginationType } from '@/models/common';
import { RootState } from '@/redux/reducers';
import mokaRequest from '@/tools/mokaRequest';
import { isNullOrUndefined } from '@/utils/common';
import { LOCALES, getI18n } from '@/utils/i18n';

import * as styles from './ApplicationsTable.cm.styl';

const i18n = getI18n();

const { EmptyNoResume } = StaticSvg;

const RECOMMEND_STATUS = {
  CANDIDATE_UN_CONFIRM: 'CANDIDATE_UN_CONFIRM', // 候选人未确认
  HR_UN_HANDLE: 'HR_UN_HANDLE', // hr未处理
  RECOMMEND_SUCCESS: 'RECOMMEND_SUCCESS', // 推荐成功
  HR_REJECT: 'HR_REJECT', //  hr拒绝
  CANDIDATE_REJECT: 'CANDIDATE_REJECT', // 候选人拒绝
  CHECK_DUP_ING: 'CHECK_DUP_ING', // 查重进行中
  CHECK_DUP_FAILE: 'CHECK_DUP_FAILE', // 查重失败
};

const isRejectedOrDeleted = ({
  stageStatus,
  headhunterConfirmed,
}: {
  stageStatus: ValueOf<typeof ADDITIONAL_STAGES>;
  headhunterConfirmed: ValueOf<typeof HEADHUNTER_CONFIRM_STATUS>;
}) =>
  [ADDITIONAL_STAGES.REJECTED, ADDITIONAL_STAGES.DELETED].includes(stageStatus) ||
  [
    HEADHUNTER_CONFIRM_STATUS.HR_REJECT,
    HEADHUNTER_CONFIRM_STATUS.CANDIDATE_REJECT,
    HEADHUNTER_CONFIRM_STATUS.SYSTEM_REJECT,
  ].includes(headhunterConfirmed);

const CONFIRM_MAPPER = {
  [HeadhunterConfirmed.CA_PENDING]: i18n.t`未反馈`,
  [HeadhunterConfirmed.CA_REJECT]: i18n.t`已拒绝`,
  [HeadhunterConfirmed.CA_EXPIRE]: i18n.t`超期未反馈`,
  [HeadhunterConfirmed.HR_PENDING]: i18n.t`未反馈`,
  [HeadhunterConfirmed.HR_AGREE]: i18n.t`已同意`,
  [HeadhunterConfirmed.HR_REJECT]: i18n.t`已拒绝`,
};

const TABLE_HEADER_PREFIX = [
  {
    key: 'name',
    title: i18n.t`候选人姓名`,
    width: '130px',
    normalCellStyle: true,
    render: (row: Application) => (
      <div className={cms.ellipsis} title={row.name}>
        {row.name}
        {!isOverseas && row.duplicateWhenUpload && <Icon name="duplicate" color="o50" />}
        {!isOverseas && row.hasSimilar && <Icon name="suspected" color="o50" />}
      </div>
    ),
  },
  {
    key: 'phone',
    dataIndex: 'phone',
    title: i18n.t`手机号`,
    width: '152px',
  },
  {
    key: 'email',
    title: i18n.t`邮箱`,
    width: '152px',
    normalCellStyle: true,
    render: (row: Application) => <AutoEllipsis>{row.email}</AutoEllipsis>,
  },
  {
    key: 'jobTitle',
    title: i18n.t`职位`,
    width: '152px',
    normalCellStyle: true,
    render: (row: Application) => <AutoEllipsis>{row.jobTitle}</AutoEllipsis>,
  },
  {
    key: 'hireMode',
    dataIndex: 'hireMode',
    title: i18n.t`招聘类型`,
    width: '110px',
    normalCellStyle: true,
    render: (row: Application) => {
      return (
        <Tag type="light" color={row.hireMode === HIRE_MODES.CAMPUS ? 'purple' : 'blue'}>
          {HIRE_MODE_NAMES[row.hireMode]}
        </Tag>
      );
    },
  },
];

const TABLE_HEADER_SUFFIX = [
  {
    key: 'appliedAt',
    title: i18n.t`推荐时间`,
    width: '120px',
    normalCellStyle: true,
    render: (row: Application) => moment(row.appliedAt).format(DATE_FORMAT),
  },
  {
    key: 'operatedAt',
    title: i18n.t`更新时间`,
    width: '120px',
    normalCellStyle: true,
    render: (row: Application) =>
      row.operatedAt ? moment(row.operatedAt).format(DATE_FORMAT) : '-',
  },
  {
    key: 'headhunterType',
    title: i18n.t`猎头类型`,
    width: 110,
    normalCellStyle: true,
    render: (row: Application) => (row.rpoBatchNo ? 'RPO' : i18n.t`猎头`),
  },
];

const CANDIDATE_TABLE_HEADER = [
  ...TABLE_HEADER_PREFIX,
  {
    key: 'confirmedStatus',
    title: i18n.t`候选人确认状态`,
    width: '130px',
    normalCellStyle: true,
    render: (row: Application) => CONFIRM_MAPPER[row.headhunterConfirmed],
  },
  {
    key: 'updatedAt',
    title: i18n.t`候选人反馈时间`,
    width: '130px',
    normalCellStyle: true,
    render: (row: Application) =>
      row.headhunterConfirmed === HeadhunterConfirmed.CA_PENDING ||
      row.headhunterConfirmed === HeadhunterConfirmed.CA_EXPIRE
        ? '-'
        : moment(row.updatedAt).format(DATE_FORMAT),
  },
  {
    key: 'expireAt',
    title: i18n.t`确认到期时间`,
    width: '120px',
    normalCellStyle: true,
    render: (row: Application) => moment(row.expireAt).format(DATE_FORMAT),
  },

  ...TABLE_HEADER_SUFFIX,
];

const HR_TABLE_HEADER = [
  ...TABLE_HEADER_PREFIX,
  {
    key: 'orgStatus',
    title: i18n.t`企业接收状态`,
    width: '120px',
    normalCellStyle: true,
    render: (row: Application) => (
      <Spacing spacing="sm">
        <span>{CONFIRM_MAPPER[row.headhunterConfirmed] ?? i18n.t`未处理`}</span>
        {row.headhunterConfirmed === HeadhunterConfirmed.HR_REJECT && (
          <Dropdown
            triggerType="hover"
            showArrow
            placement="right"
            className={styles.dropdown}
            wrapperClassName={cms.mgl4}
            popup={
              <div className={styles.detailDropdown}>
                <div className={cms.flex}>
                  <span className={styles.detailDropdownLabel}>{i18n.t`拒绝原因：`}</span>
                  <span className={sdf.bodyPrimary}>{row.rejectReason || i18n.t`无`}</span>
                </div>
              </div>
            }
          >
            <Icon name="info" color="n50" />
          </Dropdown>
        )}
      </Spacing>
    ),
  },
  ...TABLE_HEADER_SUFFIX,
];

const SUCCESS_TABLE_HEADER = [
  ...TABLE_HEADER_PREFIX,
  {
    key: 'stageStatus',
    title: i18n.t`招聘状态`,
    width: '100px',
    normalCellStyle: true,
    render: (row: Application) => (
      <Spacing spacing="sm" container>
        <div
          className={classNames({
            [cms.maxWFull]: !(
              row.stageStatus === ADDITIONAL_STAGES.REJECTED && row.archiveReasonName
            ),
            [styles.statusWidth]:
              row.stageStatus === ADDITIONAL_STAGES.REJECTED && row.archiveReasonName,
            [sdf.g60]: row.stageStatus === '已入职',
            [sdf.r60]: isRejectedOrDeleted(row),
            [sdf.y60]: row.stageStatus === ADDITIONAL_STAGES.PROCESS,
          })}
        >
          <AutoEllipsis>{row.stageStatus}</AutoEllipsis>
        </div>

        {row.stageStatus === ADDITIONAL_STAGES.REJECTED && row.archiveReasonName && (
          <Dropdown
            triggerType="hover"
            showArrow
            placement="right"
            className={styles.dropdown}
            wrapperClassName={cms.mgl4}
            popup={
              <div className={styles.detailDropdown}>
                <div className={classNames(cms.flex, cms.mgb8)}>
                  <span className={styles.detailDropdownLabel}>{i18n.t`淘汰原因：`}</span>
                  <span className={sdf.bodyPrimary}>{row.archiveReasonName}</span>
                </div>
                <div className={cms.flex}>
                  <span className={styles.detailDropdownLabel}>{i18n.t`详情：`}</span>
                  <span className={sdf.bodyPrimary}>{row.archiveDetail || i18n.t`无`}</span>
                </div>
              </div>
            }
          >
            <Icon name="info" color="n50" />
          </Dropdown>
        )}
      </Spacing>
    ),
  },
  ...TABLE_HEADER_SUFFIX,
];

interface Props {
  orgId: string;
  applyIdToComment: { [key: string]: { unread: boolean } };
  headhunterApplicationStatus: ValueOf<typeof HEADHUNTER_APPLICATION_STATUS>;
  recommendStatus: ValueOf<typeof RECOMMEND_STATUS>;
  /** 显示已淘汰申请 */
  showRejectedApplication: boolean;
  /** 显示重复申请 */
  showDuplicatedApplication?: boolean;
  /** 申请状态 */
  statusFilterValue: ValueOf<typeof ADDITIONAL_STAGES>;
  /** 职位过滤条件 */
  jobFilterId: string;
  /** 候选人过滤条件 */
  candidateNameFilterValue: string;
  /** RPO 类型过滤条件 */
  rpoRecommend: boolean;
  page: number;
  pageSize?: number;
  prohibitHeadhunterDownloadResume: boolean;
  onPageChange: (page: number) => void;
  onOpenCommentModal: (application: Application) => void;
}

const ApplicationsTable: React.FC<Props> = ({
  recommendStatus,
  page,
  pageSize = DEFAULT_PAGE_LIMIT,
  orgId,
  showRejectedApplication,
  showDuplicatedApplication = true,
  statusFilterValue,
  jobFilterId,
  candidateNameFilterValue,
  rpoRecommend,
  applyIdToComment = {},
  headhunterApplicationStatus,
  prohibitHeadhunterDownloadResume,
  onPageChange,
  onOpenCommentModal,
}) => {
  const { headhunterSetting } = useSelector((state: RootState) => ({
    headhunterSetting: state.org.setting,
  }));
  const candidateName = useKeywordDebounce(candidateNameFilterValue, 200)?.trim();
  const hiddenRealStageStatus =
    !isNullOrUndefined(headhunterApplicationStatus) &&
    headhunterApplicationStatus !== HEADHUNTER_APPLICATION_STATUS.REAL_STATUS;
  const { data, isLoading, isPreviousData } = useQuery(
    [
      'headhunterApplications',
      headhunterApplicationStatus,
      recommendStatus,
      jobFilterId,
      page,
      pageSize,
      showRejectedApplication,
      showDuplicatedApplication,
      statusFilterValue,
      candidateName,
      rpoRecommend,
    ],
    () =>
      mokaRequest<PaginationType<Application>>(
        '/api/outer/ats-jc/application/headhunter/headhunters/applications'
      )
        .post({
          orgId,
          page,
          pageSize,
          jobFilterId,
          recommendStatus,
          showRejectedApplication,
          showDuplicatedApplication,
          statusFilterValue,
          candidateNameFilterValue: candidateName,
          rpoRecommend,
        })
        .then((paginationApplications) => {
          const rows = paginationApplications.rows.map((application) => {
            if (hiddenRealStageStatus) {
              // 候选人状态为模糊类型的 默认阶段为进行中
              application.stageStatus = i18n._(ADDITIONAL_STAGES.PROCESS);
            }

            // 给候选人增加几种状态
            if (application.headhunterConfirmed === HEADHUNTER_CONFIRM_STATUS.HR_REJECT) {
              application.stageStatus = '-';
              return application;
            } else if (application.deleted) {
              application.stageStatus = i18n._(ADDITIONAL_STAGES.DELETED);
              return application;
            }
            if (application.hired) {
              application.stageStatus = i18n._(ADDITIONAL_STAGES.HIRED);
            } else if (application.archived) {
              application.stageStatus = i18n._(ADDITIONAL_STAGES.REJECTED);
            }
            return application;
          });
          return {
            ...paginationApplications,
            rows,
          };
        }),
    {
      keepPreviousData: true,
      enabled: !!orgId,
    }
  );

  // 加载中
  if (isLoading) {
    return (
      <div className="load-more">
        <Loading className={cms.pdy16} message={i18n.t`加载中`} />
      </div>
    );
  }

  // 无数据
  if (!data?.rows?.length) {
    return <Empty image={<EmptyNoResume />} title={i18n.t`无推荐历史`} />;
  }

  const totalPage = Math.ceil(data.total / data.pageSize);

  let headers = SUCCESS_TABLE_HEADER;
  if (
    recommendStatus === RECOMMEND_STATUS.CANDIDATE_UN_CONFIRM ||
    recommendStatus === RECOMMEND_STATUS.CANDIDATE_REJECT
  ) {
    headers =
      recommendStatus === RECOMMEND_STATUS.CANDIDATE_UN_CONFIRM
        ? [
            ...TABLE_HEADER_PREFIX,
            {
              key: 'expireAt',
              title: i18n.t`确认到期时间`,
              width: '120px',
              normalCellStyle: true,
              render: (row: Application) => moment(row.expireAt).format(DATE_FORMAT),
            },
            ...TABLE_HEADER_SUFFIX,
          ]
        : CANDIDATE_TABLE_HEADER;
  } else if (
    recommendStatus === RECOMMEND_STATUS.CHECK_DUP_ING ||
    recommendStatus === RECOMMEND_STATUS.CHECK_DUP_FAILE
  ) {
    headers = [...TABLE_HEADER_PREFIX, ...TABLE_HEADER_SUFFIX];
  } else if (
    recommendStatus === RECOMMEND_STATUS.HR_UN_HANDLE ||
    recommendStatus === RECOMMEND_STATUS.HR_REJECT
  ) {
    headers = HR_TABLE_HEADER;
  }

  headers = [
    ...headers,
    {
      key: 'operation',
      freeze: 'right',
      title: i18n.t`操作`,
      normalCellStyle: true,
      width: i18n.language === LOCALES.ZH_CN ? '240px' : '420px',
      render: (row: Application) => {
        let currentCanUpdateHeadhunterResume = false;
        let currentCanUpdateRpoResume = false;
        if (row.hireMode === HIRE_MODES.SOCIAL) {
          currentCanUpdateHeadhunterResume =
            headhunterSetting?.socialSetting?.headhunterUpdateResumeSwitch;
          currentCanUpdateRpoResume = headhunterSetting?.socialSetting?.rpoUpdateResumeSwitch;
        }
        if (row.hireMode === HIRE_MODES.CAMPUS) {
          currentCanUpdateHeadhunterResume =
            headhunterSetting?.campusSetting?.headhunterUpdateResumeSwitch;
          currentCanUpdateRpoResume = headhunterSetting?.campusSetting?.rpoUpdateResumeSwitch;
        }

        return (
          <Spacing spacing="lg">
            {!prohibitHeadhunterDownloadResume && row.downloadUrl && (
              <a href={row.downloadUrl} target="_self">
                <Button type="text">{i18n.t`下载简历`}</Button>
              </a>
            )}

            {(recommendStatus === RECOMMEND_STATUS.RECOMMEND_SUCCESS ||
              row.headhunterConfirmed === HEADHUNTER_CONFIRM_STATUS.HR_UN_HANDLE) &&
              !isRejectedOrDeleted(row) &&
              ((row.rpoBatchNo && currentCanUpdateRpoResume) ||
                (!row.rpoBatchNo && currentCanUpdateHeadhunterResume)) && (
                <Link to={`/headhunters/${orgId}/update/${row.id}`}>
                  <Button type="text">{i18n.t`更新简历`}</Button>
                </Link>
              )}
            {(row.headhunterConfirmed === HEADHUNTER_CONFIRM_STATUS.HR_ACCEPT ||
              row.headhunterConfirmed === HEADHUNTER_CONFIRM_STATUS.HR_UN_HANDLE ||
              row.headhunterConfirmed === HEADHUNTER_CONFIRM_STATUS.HR_REJECT) && (
              <Button
                className={styles.commentBtn}
                type="text"
                onClick={() => onOpenCommentModal(row)}
              >
                {applyIdToComment[row.id] && applyIdToComment[row.id].unread ? (
                  <>
                    <Tag className={styles.commentTip} size="sm" type="light" color="red">
                      {i18n.t`HR@了你`}
                    </Tag>
                    <span className={styles.commentHr}>{i18n.t`与HR沟通`} </span>
                  </>
                ) : (
                  i18n.t`与HR沟通`
                )}
              </Button>
            )}
          </Spacing>
        );
      },
    },
  ];
  return (
    <>
      <NewTable
        className={cms.mgt8}
        setRowClassName={() => styles.row}
        width="100%"
        rowKey="id"
        tableAdaptMode="scroll"
        headers={headers}
        rows={data.rows}
        columnResizeEnabled
        isLoading={isPreviousData}
      />
      {totalPage > 1 && (
        <Pagination pageSize={pageSize} total={data.total} current={page} onChange={onPageChange} />
      )}
    </>
  );
};
export default ApplicationsTable;
